<form [formGroup]="form" (ngSubmit)="submitForm()" class="flex w-full flex-col gap-(--gap-normal)">
  <div
    class="content overflow-y-auto p-(--space-1) scrollbar-thin max-h-6/10 flex flex-col gap-(--gap-relaxed) mb-(--space-4) overflow-visible">
    <div class="flex w-full flex-col gap-(--gap-relaxed) flex-wrap">
      @if (entityDefinition()?.fields) {
        @for (row of getFormFieldRows(); track $index) {
          <div [class]="row.length > 1 ? 'flex flex-row gap-(--gap-relaxed) flex-wrap' : 'contents'">
            @for (field of row; track field.field) {
              <div [class]="row.length > 1 ? 'flex-1 min-w-50' : 'contents'">
                @if (field.type === 'text') {
                  <ces-text-field
                    [id]="field.field"
                    [formControlName]="field.field"
                    [label]="getFieldLabel(field)"
                    [required]="field.required"
                    [minLength]="field.minLength"
                    [maxLength]="field.maxLength"
                    [unicityArray]="field.unicityArray"
                    [unicityKey]="field.unicityKey"
                    [unicityExcludeValue]="field.unicityExcludeValue"
                    [externalErrors]="getExternalErrorsForField(field.field)"
                    class="field-set"
                    inputClass="w-full"
                    labelClass="" />
                }

                @if (field.type === 'textarea') {
                  <ces-text-field
                    [id]="field.field"
                    [formControlName]="field.field"
                    [label]="getFieldLabel(field)"
                    [required]="field.required"
                    [minLength]="field.minLength"
                    [maxLength]="field.maxLength"
                    [externalErrors]="getExternalErrorsForField(field.field)"
                    type="textarea"
                    [rows]="3"
                    class="field-set"
                    inputClass="w-full"
                    labelClass="" />
                }

                @if (field.type === 'number') {
                  <div class="field-set flex flex-col gap-(--gap-normal) flex-wrap">
                    <label [for]="field.field"
                      >{{ getFieldLabel(field) }}
                      @if (field.required) {
                        <sup>*</sup>
                      }
                    </label>
                    <p-inputNumber
                      [id]="field.field"
                      [formControlName]="field.field"
                      [min]="field.minValue"
                      [max]="field.maxValue"
                      [attr.aria-required]="field.required || null"
                      [attr.aria-invalid]="(form.get(field.field)?.invalid && form.get(field.field)?.dirty) || null" />
                    <div class="validation-error-placeholder">
                      @if (form.get(field.field)?.invalid && form.get(field.field)?.dirty) {
                        <small class="text-red-500" role="alert">
                          @if (form.get(field.field)?.errors?.['required']) {
                            <span>{{ 'validation.required' | transloco }}</span>
                          }
                          @if (form.get(field.field)?.errors?.['min']) {
                            <span>{{ 'validation.min' | transloco: { min: field.minValue } }}</span>
                          }
                          @if (form.get(field.field)?.errors?.['max']) {
                            <span>{{ 'validation.max' | transloco: { max: field.maxValue } }}</span>
                          }
                        </small>
                      }
                    </div>
                  </div>
                }

                @if (field.type === 'boolean') {
                  <div class="field-set flex flex-col gap-(--gap-normal) flex-wrap">
                    <div class="flex align-items-center gap-(--space-2)">
                      <p-toggleswitch [id]="field.field" [formControlName]="field.field" />
                      <label [for]="field.field">{{ getFieldLabel(field) }}</label>
                      @if (field.tooltipKey) {
                        <i
                          role="img"
                          [attr.aria-label]="field.tooltipKey | transloco"
                          class="pi pi-info-circle text-sm text-(color:--text-secondary) cursor-help"
                          [pTooltip]="field.tooltipKey | transloco"
                          tooltipPosition="top"></i>
                      }
                    </div>
                  </div>
                }

                @if (field.type === 'select') {
                  <div class="field-set flex flex-col gap-(--gap-normal) flex-wrap">
                    @if (field.storeType || field.values) {
                      <ces-select-field
                        [emptyFilterMessage]="field.emptyFilterMessage ?? ''"
                        [emptyMessage]="field.emptyMessage ?? ''"
                        [filter]="field.filterable ?? true"
                        [formControlName]="field.field"
                        [label]="getFieldLabel(field)"
                        [options]="field.storeType ? getTypeOptions(field.storeType) : (field.values ?? [])"
                        [placeholder]="getFieldPlaceholder(field)"
                        [required]="field.required" />
                    } @else {
                      <ng-container *ngTemplateOutlet="selectErrorTemplate" />
                    }
                  </div>
                }

                @if (field.type === 'hour') {
                  <div class="field-set flex flex-col gap-(--gap-normal) flex-wrap">
                    <ces-hourpicker-field [formControlName]="field.field" [label]="getFieldLabel(field)" [required]="field.required" />
                  </div>
                }

                @if (field.type === 'datetime') {
                  <div class="field-set flex flex-col gap-(--gap-normal) flex-wrap">
                    <label [for]="field.field"
                      >{{ getFieldLabel(field) }}
                      @if (field.required) {
                        <sup>*</sup>
                      }
                    </label>
                    <ces-datepicker
                      [id]="field.field"
                      [formControlName]="field.field"
                      [autofocus]="false"
                      [attr.aria-required]="field.required || null"
                      [attr.aria-invalid]="(form.get(field.field)?.invalid && form.get(field.field)?.dirty) || null" />
                    <div class="validation-error-placeholder">
                      @if (form.get(field.field)?.errors?.['uniqueDate']) {
                        <!-- uniqueDate only ever comes from a uniqueness validator on a real value,
                             so surface it immediately (even on the pristine default date) rather
                             than waiting for the user to dirty the control. -->
                        <small class="text-red-500" role="alert">
                          <span>{{ field.uniqueErrorKey ?? 'validation.unique' | transloco }}</span>
                        </small>
                      } @else if (form.get(field.field)?.invalid && form.get(field.field)?.dirty) {
                        <small class="text-red-500" role="alert">
                          @if (form.get(field.field)?.errors?.['required']) {
                            <span>{{ 'validation.required' | transloco }}</span>
                          }
                          @if (form.get(field.field)?.errors?.['invalidDate']) {
                            <span>{{ 'validation.date.invalid' | transloco }}</span>
                          }
                        </small>
                      }
                    </div>
                  </div>
                }

                @if (field.type === 'multi') {
                  <div class="field-set flex flex-col gap-(--gap-normal) flex-wrap">
                    @if (field.storeType || field.values) {
                      <ces-select-field
                        [emptyFilterMessage]="field.emptyFilterMessage ?? ''"
                        [emptyMessage]="field.emptyMessage ?? ''"
                        [filter]="field.filterable ?? true"
                        [formControlName]="field.field"
                        [label]="getFieldLabel(field)"
                        [multiSelect]="true"
                        [options]="field.storeType ? getTypeOptions(field.storeType) : (field.values ?? [])"
                        [placeholder]="getFieldPlaceholder(field)"
                        [required]="field.required" />
                    } @else {
                      <ng-container *ngTemplateOutlet="selectErrorTemplate" />
                    }
                  </div>
                }

                @if (field.type === 'hidden') {
                  <div class="field-set hidden">
                    <input
                      autocomplete="off"
                      spellcheck="false"
                      autocorrect="off"
                      autocapitalize="off"
                      data-gramm="false"
                      [id]="field.field"
                      type="hidden"
                      [formControlName]="field.field" />
                  </div>
                }
              </div>
            }
          </div>
        }
      }

      <ng-container *ngTemplateOutlet="additionalFieldsTemplate() ?? null; context: { $implicit: form }" />

      @if (errorMessage) {
        <div class="text-red-500 mb-(--space-3)">
          {{ errorMessage }}
        </div>
      }
    </div>
  </div>
  <ces-dialog-footer [submitDisabled]="form.invalid || submitting" (onCancel)="cancel()" (onSubmit)="submitForm()">
    @if (!isCreateMode && canShowDeleteButton() && canDeleteEntity()) {
      <ces-delete-button
        footer-start
        [label]="entityDefinition()?.messages?.buttons?.delete() ?? '' | transloco"
        [confirmationConfig]="deleteConfig()"
        [pTooltip]="canDeleteEntityMessages()"
        tooltipPosition="bottom"
        (confirm)="deleteEntityAndClose()" />
    }
    @if (!isCreateMode && canShowDeleteButton() && !canDeleteEntity()) {
      <ces-delete-button
        footer-start
        [label]="entityDefinition()?.messages?.buttons?.delete() ?? '' | transloco"
        [disabled]="true"
        [pTooltip]="canDeleteEntityMessages()"
        tooltipPosition="bottom"
        (confirm)="deleteEntityAndClose()" />
    }
  </ces-dialog-footer>
</form>
<ng-template #selectErrorTemplate>
  <div class="bg-red-100 border-2 border-red-300 text-sm rounded-(--radius-md) p-(--space-1) text-center">
    {{ 'common.errors.fieldDefinitionMissingOptions' | transloco }}
  </div>
</ng-template>
